/**
 * `describeChange` — turn one `NodeChange` (B3.5 compare) into the FACTUAL
 * phrases the diff renders. Pure, dependency-free over the wire shape + the
 * `TASK_STATES` vocabulary. The single source of truth for "what moved on a
 * node between the two points," so the diff tree + the side summary phrase
 * changes identically.
 *
 * Issue #765 (the deeper time machine, B3.5).
 *
 * Brand firewall (§A9 — compare is a surveillance-risk vector): every phrase
 * here is about THE NODE — "moved to Done", "re-assigned", "moved to a new
 * parent". It records WHAT changed, never WHO changed it (provenance lives in
 * per-node history, never aggregated here). There is no count, rate, or
 * comparison about any person. The banlist applies to every string.
 *
 * Mirrors `history/describeEvent.ts`: factual + calm, never evaluative
 * ("re-assigned", never "reassigned away from a slow worker").
 */

import type { NodeChange } from "../api/useTaskCompare";
import { type TaskState, taskStateStyle } from "../tree/state";

function isTaskState(v: unknown): v is TaskState {
  return v === "planned" || v === "in_progress" || v === "blocked" || v === "done";
}

/** A single described change dimension, ready to render. */
export interface ChangeBadge {
  /** A short factual phrase, e.g. "Not started → Done", "re-assigned", "moved". */
  readonly text: string;
  /** For a state move: the destination state, so the badge can tint with its colour. */
  readonly toState: TaskState | null;
}

/**
 * Describe the dimensions that moved on a node, in a stable order: state
 * first (the headline), then assignment, then parent. Returns one badge per
 * moved dimension — a node can change in more than one way at once.
 */
export function describeChange(change: NodeChange): ChangeBadge[] {
  const badges: ChangeBadge[] = [];

  if (change.state_changed) {
    const from = isTaskState(change.state_from) ? taskStateStyle(change.state_from).label : null;
    const to = isTaskState(change.state_to) ? taskStateStyle(change.state_to).label : null;
    const toState = isTaskState(change.state_to) ? change.state_to : null;
    if (from && to) badges.push({ text: `${from} → ${to}`, toState });
    else if (to) badges.push({ text: `Moved to ${to}`, toState });
    else badges.push({ text: "State changed", toState: null });
  }

  // Re-assignment: WHAT changed (the assignment-set moved), never WHO or how
  // much. Deliberately not naming people here — the diff is about the plan.
  if (change.reassigned) {
    badges.push({ text: "re-assigned", toState: null });
  }

  // Re-parent: the node moved in the tree's structure.
  if (change.reparented) {
    badges.push({ text: "moved to a new parent", toState: null });
  }

  return badges;
}

/** The compact state-arrow glyph for a change badge ("◐ → ●"), or null. */
export function stateArrowGlyph(change: NodeChange): string | null {
  if (!change.state_changed) return null;
  const from = isTaskState(change.state_from) ? taskStateStyle(change.state_from).glyph : "·";
  const to = isTaskState(change.state_to) ? taskStateStyle(change.state_to).glyph : "·";
  return `${from} → ${to}`;
}
